import React, {
  type InputHTMLAttributes,
  forwardRef,
  useState,
  useEffect,
} from 'react';
import { MdCheck, MdRemove } from 'react-icons/md';
import { Typewriter } from '../../contexts/TypewriterContext';

type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> & {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  labelPosition?: 'left' | 'right';
  variant?: 'default';
  indeterminate?: boolean;
  wrapperClassName?: string;
  effect?: 'typewriter' | 'none';
};

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    size = 'md',
    label,
    labelPosition = 'right',
    className = '',
    wrapperClassName = '',
    disabled = false,
    indeterminate = false,
    checked: controlledChecked = false,
    onChange,
    effect = 'none',
    ...rest
  } = props;

  const [internalChecked, setInternalChecked] = useState(controlledChecked);
  const isControlled = 'checked' in props;
  const checked = isControlled ? controlledChecked : internalChecked;

  useEffect(() => {
    if (isControlled) {
      setInternalChecked(controlledChecked);
    }
  }, [controlledChecked, isControlled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) {
      setInternalChecked(e.target.checked);
    }
    onChange?.(e);
  };

  const sizeClasses = {
    sm: 'w-4 h-4 rounded-sm',
    md: 'w-5 h-5 rounded-md',
    lg: 'w-6 h-6 rounded-lg',
  };

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const baseClasses =
    'border-2 transition-all duration-200 cursor-pointer focus:outline-none';

  const stateClass = `bg-transparent border-purple-50 bg-white hover:bg-purple-25 focus:bg-white focus:ring-2 focus:ring-primary-200 ${checked || indeterminate ? '!border-primary-400 !bg-primary-400 !text-white' : ''}`;

  const disabledClass = 'border-gray-200 !cursor-not-allowed';

  const labelClasses = {
    default: 'text-gray-900 cursor-pointer',
    disabled: 'text-gray-400 !cursor-not-allowed',
  };

  const computedInputClasses = `${baseClasses} ${sizeClasses[size]} ${className}`;
  const computedLabelClasses = `${labelClasses[disabled ? 'disabled' : 'default']} select-none`;

  const checkboxElement = (
    <div className='relative inline-flex items-center'>
      <input
        ref={ref}
        type='checkbox'
        className='sr-only'
        disabled={disabled}
        checked={checked}
        onChange={handleChange}
        {...rest}
      />
      <div
        role='checkbox'
        tabIndex={0}
        aria-checked={checked}
        className={`
          ${computedInputClasses}
          ${disabled ? disabledClass : stateClass}
        `}
      >
        {checked && !indeterminate && (
          <MdCheck className={`${iconSizeClasses[size]} text-white`} />
        )}
        {indeterminate && (
          <MdRemove className={`${iconSizeClasses[size]} text-white`} />
        )}
      </div>
    </div>
  );

  if (!label) {
    return <div className='group'>{checkboxElement}</div>;
  }

  return (
    <label
      className={`group flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${wrapperClassName}`}
    >
      {labelPosition === 'left' && (
        effect === 'typewriter' ? (
          <Typewriter as={'span'} text={label} className={computedLabelClasses} />
        ) : (
          <span className={computedLabelClasses}>{label}</span>
        )
      )}
      {checkboxElement}
      {labelPosition === 'right' && (
        effect === 'typewriter' ? (
          <Typewriter as={'span'} text={label} className={computedLabelClasses} />
        ) : (
          <span className={computedLabelClasses}>{label}</span>
        )
      )}
    </label>
  );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
